import { PictogramName, SpotIconName } from '@cbhq/cds-illustrations';
import { SidebarItemProps } from '@cbhq/cds-web/navigation/SidebarItem';

import {
  AGENTKIT_DOCS_LINK,
  CLOUD_DOCS,
  PRIME_DOCS_LINK,
  TRADE_DOCS_LINK,
} from ':cloud/utils/links';
import { AppRoute } from ':cloud/utils/routes';

/** These nav items have a flyout with children. They don't have landing pages. */
type ParentNavItemId =
  | 'wallets-parent'
  | 'trading-parent'
  | 'onchain-tools-parent'
  | 'payments-parent';

/** These nav items are nested beneath parent nav items */
type PlatformAPINavItemId =
  | 'advanced-trade'
  | 'agentkit'
  | 'base' // Node
  | 'bundler-paymaster'
  | 'faucet'
  | 'onchainkit'
  | 'onramp'
  | 'wallet-funding'
  | 'wallet-api'
  | 'send-receive'
  | 'trade'
  | 'verifications'
  | 'waas_consumer' // Smart Wallet
  | 'wallet_history'
  | 'base-l3'
  | 'base-appchains';

/** These nav items are standalone since they're the only ones in their category. */
type StandalonePlatformAPINavItemId = 'pay' | 'participate' | 'staking';

type ProductAPINavItemId = 'commerce' | 'exchange' | 'prime' | 'sign-in';

type PrimaryNavItemId =
  | 'access'
  | 'api'
  | 'billing'
  | 'dashboard'
  | 'documentation'
  | 'earnings'
  | 'project-dashboard';

export type NavItemId =
  | PrimaryNavItemId
  | ParentNavItemId
  | PlatformAPINavItemId
  | StandalonePlatformAPINavItemId
  | ProductAPINavItemId
  | 'projects';

export interface NavItem extends SidebarItemProps {
  id: NavItemId;
  pageTitle?: string;
  detail?: string;
}

export type ParentNavItem = NavItem & { childIds: NavItemId[] };

interface NavItemChildBase {
  id: NavItemId;
  pageTitle?: string;
  title: string;
  detail?: string;
  onPress?: () => void;
  to?: string;
  parentId?: string;
}
export interface NavItemChildWithPictogram extends NavItemChildBase {
  iconType: 'pictogram';
  icon: PictogramName;
}
export interface NavItemChildWithSpotIcon extends NavItemChildBase {
  iconType: 'spotIcon';
  icon: SpotIconName;
}

/** Used in Side Nav flyout menus */
export type NavItemChild = NavItemChildWithPictogram | NavItemChildWithSpotIcon;

export const primaryNavItems: NavItem[] = [
  {
    title: 'Project',
    id: 'project-dashboard',
    icon: 'home',
    to: AppRoute.Projects.Home,
  },
];

export const platformAPINavItems: NavItemChild[] = [
  {
    title: 'Node',
    id: 'base',
    icon: 'baseLogoNavigation',
    iconType: 'pictogram',
    to: AppRoute.Products.Base,
    detail: 'Free mainnet RPC endpoint for Base',
    parentId: 'onchain-tools-parent',
  },
  {
    title: 'Faucet',
    id: 'faucet',
    icon: 'faucetNavigation',
    iconType: 'pictogram',
    to: AppRoute.Products.Faucet,
    detail: 'Get free Base Sepolia ETH',
    parentId: 'onchain-tools-parent',
  },
  {
    title: 'Paymaster',
    id: 'bundler-paymaster',
    icon: 'gasFees',
    iconType: 'pictogram',
    to: AppRoute.Products.BundlerAndPaymaster,
    detail: 'Build custom gasless experiences on Base',
    parentId: 'onchain-tools-parent',
  },
  {
    title: 'Smart Wallet',
    id: 'waas_consumer',
    icon: 'wallet',
    iconType: 'spotIcon',
    to: AppRoute.Products.Wallets,
    detail: 'Let your users create a wallet in seconds',
    parentId: 'wallets-parent',
  },
  {
    title: 'Send & Receive',
    id: 'send-receive',
    icon: 'globalPayments',
    iconType: 'pictogram',
    to: AppRoute.Products.SendReceive,
    detail: 'Make and accept crypto payments',
    parentId: 'payments-parent',
  },
  {
    title: 'Onramp',
    id: 'onramp',
    icon: 'paySDKProduct',
    iconType: 'spotIcon',
    to: AppRoute.Products.Onramp,
    detail: 'Enable users to fund their wallets',
    parentId: 'payments-parent',
  },
  {
    title: 'Wallet Funding',
    id: 'wallet-funding',
    icon: 'productWallet',
    iconType: 'spotIcon',
    to: AppRoute.Products.WalletFunding,
    detail: 'Send funds to your API wallets',
    parentId: 'payments-parent',
  },
  {
    title: 'Data',
    id: 'wallet_history',
    pageTitle: 'Data',
    icon: 'decentralizedWeb3',
    iconType: 'pictogram',
    to: AppRoute.Products.OnchainData,
    detail: 'Access wallet balances and transactions',
    parentId: 'onchain-tools-parent',
  },
  {
    title: 'Verifications',
    id: 'verifications',
    icon: 'idVerification',
    iconType: 'spotIcon',
    to: AppRoute.Products.Verifications,
    detail: "Verify your users' wallets with Coinbase",
    parentId: 'onchain-tools-parent',
  },
  {
    title: 'OnchainKit',
    id: 'onchainkit',
    icon: 'easyToUse',
    iconType: 'pictogram',
    to: AppRoute.Products.Boat,
    detail: 'React components for building onchain apps',
    parentId: 'onchain-tools-parent',
  },
  {
    title: 'AgentKit',
    id: 'agentkit',
    icon: 'nftAvatar',
    iconType: 'pictogram',
    to: AGENTKIT_DOCS_LINK,
    detail: 'Build AI agents with onchain functionality',
    parentId: 'onchain-tools-parent',
  },
  {
    title: 'Base L3 Launcher',
    id: 'base-l3',
    icon: 'developerPlatformNavigation',
    iconType: 'pictogram',
    to: AppRoute.Products.BaseL3,
    detail: 'Unlock fast and cheap transactions at scale with dedicated blockspace',
    parentId: 'onchain-tools-parent',
  },
  {
    title: 'Base Appchains',
    id: 'base-appchains',
    icon: 'developerPlatformNavigation',
    iconType: 'pictogram',
    to: AppRoute.Products.BaseAppchains,
    detail: 'Unlock fast and cheap transactions at scale with dedicated blockspace',
    parentId: 'onchain-tools-parent',
  },
  {
    title: 'Wallet API',
    id: 'wallet-api',
    icon: 'walletAsAServiceProduct',
    iconType: 'spotIcon',
    to: AppRoute.Products.WalletAPI,
    detail: 'Spin up wallets you own, fund your wallet, and do mass payouts',
    parentId: 'wallets-parent',
  },
  {
    title: 'Trade',
    id: 'trade',
    icon: 'fiat',
    iconType: 'pictogram',
    to: TRADE_DOCS_LINK,
    detail: 'Add trading and swap functionality to your app',
    parentId: 'trading-parent',
  },
];

export const standalonePlatformAPINavItems: NavItem[] = [
  {
    title: 'Staking',
    id: 'staking',
    icon: 'staking',
    to: AppRoute.Products.Staking,
    detail: 'Enable staking rewards for you and your customers',
  },
  {
    title: 'Validators',
    id: 'participate',
    icon: 'participate',
    to: AppRoute.Participate.Home,
    detail: 'Secure blockchain staking solutions',
  },
];

export const productAPINavItems: NavItem[] = [
  {
    title: 'Coinbase App',
    id: 'sign-in',
    icon: 'coinbase',
    to: AppRoute.Products.App,
    detail: 'Transfer and track Coinbase account activity.',
  },
  {
    title: 'Coinbase Advanced Trade',
    id: 'advanced-trade',
    icon: 'advancedTradeProduct',
    to: AppRoute.Products.AdvancedTrade,
    detail: 'Automate trading & access market insights',
  },
  {
    title: 'Coinbase Exchange',
    id: 'exchange',
    icon: 'exchangeProduct',
    to: AppRoute.Products.Exchange,
    detail: 'Direct access to our US spot exchange and market data',
  },
  {
    title: 'Coinbase Prime',
    id: 'prime',
    icon: 'primePoduct',
    to: PRIME_DOCS_LINK,
    detail: 'High-volume trading and custody for institutional traders',
  },
  {
    title: 'Coinbase Commerce',
    id: 'commerce',
    icon: 'commerceProduct',
    to: AppRoute.Products.Commerce,
    detail: 'Start accepting crypto payments',
  },
];

export const platformAPIParentNavItems: ParentNavItem[] = [
  {
    title: 'Wallets',
    id: 'wallets-parent',
    icon: 'wallet',
    childIds: ['wallet-api', 'waas_consumer'],
  },
  {
    title: 'Onchain Tools',
    id: 'onchain-tools-parent',
    icon: 'participateProduct',
    childIds: [
      'base',
      'faucet',
      'bundler-paymaster',
      'wallet_history',
      'verifications',
      'onchainkit',
      'agentkit',
      'base-l3',
      'base-appchains',
    ],
  },
  {
    title: 'Payments',
    id: 'payments-parent',
    icon: 'pay',
    childIds: ['send-receive', 'onramp', 'wallet-funding'],
  },
  // Waiting on clarification on what child items should be included
  {
    title: 'Trading',
    id: 'trading-parent',
    icon: 'trading',
    childIds: [],
  },
];

export const mobileHamburgerItems: readonly NavItem[] = [
  {
    title: 'Documentation',
    id: 'documentation',
    icon: 'documentation',
    to: CLOUD_DOCS,
  },
];
